/**
 * Settings Context - Global settings state management with caching
 * @file src/web/shared/context/SettingsContext.tsx
 *
 * @llm-rule Cache settings in localStorage for 24 hours
 * @llm-rule Invalidate cache when updatedAt timestamp changes
 * @llm-rule Fetch from backend only when cache is stale or empty
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { config } from '../../features/auth/config';

interface Settings {
  id: number;
  notification1: string | null;
  notification2: string | null;
  notification3: string | null;
  update1Title: string | null;
  update1Description: string | null;
  update2Title: string | null;
  update2Description: string | null;
  update3Title: string | null;
  update3Description: string | null;
  update4Title: string | null;
  update4Description: string | null;
  update5Title: string | null;
  update5Description: string | null;
  advertisementImageUrl: string | null;
  heroImageUrl: string | null;
  contactAddress: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  contactHours: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CachedSettings {
  data: Settings;
  timestamp: number;
  updatedAt: string;
}

interface SettingsContextType {
  settings: Settings | null;
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const CACHE_KEY = 'tuitionpoint_settings';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Get cached settings from localStorage
   */
  const getCachedSettings = (): CachedSettings | null => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const parsed: CachedSettings = JSON.parse(cached);
      const now = Date.now();

      // Check if cache is still valid (within 24 hours)
      if (now - parsed.timestamp < CACHE_DURATION) {
        return parsed;
      }

      // Cache expired, remove it
      localStorage.removeItem(CACHE_KEY);
      return null;
    } catch (err) {
      console.error('Failed to parse cached settings:', err);
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
  };

  /**
   * Save settings to localStorage cache
   */
  const setCachedSettings = (data: Settings) => {
    try {
      const cached: CachedSettings = {
        data,
        timestamp: Date.now(),
        updatedAt: data.updatedAt,
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
    } catch (err) {
      console.error('Failed to cache settings:', err);
    }
  };

  /**
   * Fetch settings from backend
   */
  const fetchSettings = async (): Promise<Settings | null> => {
    try {
      const response = await fetch(`${config.api.baseUrl}/api/settings`, {
        headers: {
          'Content-Type': 'application/json',
          [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      return result.settings;
    } catch (err: any) {
      console.error('Failed to fetch settings:', err);
      throw err;
    }
  };

  /**
   * Load settings (from cache or backend)
   */
  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Try to get from cache first
      const cached = getCachedSettings();

      if (cached) {
        console.log('✅ Using cached settings');
        setSettings(cached.data);
        setLoading(false);

        // Optionally check if backend has newer version (in background)
        fetchSettings().then((backendSettings) => {
          if (backendSettings && backendSettings.updatedAt !== cached.updatedAt) {
            console.log('🔄 Backend has newer settings, updating cache');
            setSettings(backendSettings);
            setCachedSettings(backendSettings);
          }
        }).catch(() => {
          // Silently fail background check
        });
      } else {
        // No cache, fetch from backend
        console.log('📥 Fetching settings from backend');
        const backendSettings = await fetchSettings();

        if (backendSettings) {
          setSettings(backendSettings);
          setCachedSettings(backendSettings);
        }
        setLoading(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load settings');
      setLoading(false);
    }
  }, []);

  /**
   * Refresh settings (force fetch from backend)
   */
  const refreshSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const backendSettings = await fetchSettings();

      if (backendSettings) {
        setSettings(backendSettings);
        setCachedSettings(backendSettings);
      }
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to refresh settings');
      setLoading(false);
    }
  }, []);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  return (
    <SettingsContext.Provider value={{ settings, loading, error, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

/**
 * Hook to use settings context
 */
export const useSettingsContext = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettingsContext must be used within a SettingsProvider');
  }
  return context;
};
